import { c as _c } from "react/compiler-runtime";
import type { Cell, Renderable, Row, SortDirection, Table } from '@tanstack/react-table';
import type { ReactElement } from 'react';
import { useIntersectionObserver } from '@react-hookz/web';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import { sum } from 'es-toolkit';
import ms from 'ms';
import nstr from 'nstr';
import * as React from 'react';
import { Fragment, useRef, useState } from 'react';
import type { DivProps } from '@/components/ui/html-elements';
import type { RowSelectionChangeFn } from '@/types/utils';
import { SortIcon } from '@/components/my-icon/sort-icon';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Slot } from '@/components/ui/slot';
import { cn } from '@/lib/utils';
import { useClone, useRect, useRetimer } from '@sub-vocab/utils/hooks';
type GroupHeader<T> = ReturnType<Table<T>['getHeaderGroups']>[number]['headers'][number];
export const HEAD_HEIGHT = 30;
function FlexRender(t0) {
  const $ = _c(3);
  const {
    comp,
    props
  } = t0;
  const t1 = useClone(props);
  let t2;
  if ($[0] !== comp || $[1] !== t1) {
    t2 = flexRender(comp, t1);
    $[0] = comp;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function TableHeader(t0) {
  const $ = _c(14);
  let children;
  let props;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className: t1,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = style;
    $[4] = t1;
  } else {
    children = $[1];
    props = $[2];
    style = $[3];
    t1 = $[4];
  }
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[5] !== style) {
    t2 = {
      "--z-index": 999999999,
      "--height": `${HEAD_HEIGHT}px`,
      ...style
    };
    $[5] = style;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cn("sticky top-0 z-(--z-index) h-(--height) bg-background px-0", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t2 || $[12] !== t3) {
    t4 = <thead style={t2} className={t3} {...props}>{children}</thead>;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function TableHeaderCellRender(t0) {
  const $ = _c(5);
  const {
    header
  } = t0;
  let t1;
  if ($[0] !== header) {
    t1 = header.getContext();
    $[0] = header;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = useClone(t1);
  let t2;
  if ($[2] !== header || $[3] !== props) {
    t2 = header.isPlaceholder ? <TableHeaderCell header={header} /> : <FlexRender comp={header.column.columnDef.header} props={props} />;
    $[2] = header;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function TableHeaderCell(t0) {
  const $ = _c(9);
  const {
    header: t1,
    children,
    className: t2
  } = t0;
  const {
    id,
    colSpan
  } = t1;
  const className = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cn("group/th border-y border-solid border-border bg-background p-0 text-xs font-normal whitespace-nowrap", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children) {
    t4 = <Slot className="flex size-full h-7 cursor-pointer items-center">{children}</Slot>;
    $[2] = children;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== colSpan || $[5] !== id || $[6] !== t3 || $[7] !== t4) {
    t5 = <th key={id} colSpan={colSpan} className={t3}>{t4}</th>;
    $[4] = colSpan;
    $[5] = id;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export function TableDataCell(t0) {
  const $ = _c(8);
  const {
    cell: t1,
    children,
    className: t2
  } = t0;
  const {
    id
  } = t1;
  const className = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cn("h-8 border-t border-solid border-t-border-td p-0 text-sm group-first-of-type/tr:border-t-transparent group-data-boundary/tr:border-b-border-td", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children) {
    t4 = children?.type === Fragment ? children : <Slot className="flex size-full items-center">{children}</Slot>;
    $[2] = children;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== id || $[5] !== t3 || $[6] !== t4) {
    t5 = <td key={id} className={t3}>{t4}</td>;
    $[4] = id;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function HeaderTitle(t0) {
  const $ = _c(13);
  const {
    title,
    className,
    isSorted
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex grow items-center select-none", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isSorted ? "font-semibold" : "";
  let t3;
  if ($[2] !== t2) {
    t3 = clsx("grow before:invisible before:block before:h-0 before:overflow-hidden before:font-bold before:content-[attr(data-title)]", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== title) {
    t4 = <span data-title={title} className={t3}>{title}</span>;
    $[4] = t3;
    $[5] = title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isSorted) {
    t5 = <SortIcon isSorted={isSorted} />;
    $[7] = isSorted;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div className={t1}>{t4}{t5}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
type RowProp<T> = {
  row: Row<T>;
  onRowSelectionChange?: RowSelectionChangeFn<T>;
} & Partial<ExpandProp>;
type ExpandProp = {
  children: ReactElement;
  index: number;
  root: React.RefObject<HTMLDivElement | null> | null;
};
const ANIM_DURATION = ms('0.3s');
const SHADOW_DURATION = ms('0.4s');
export function TableRow(t0) {
  const $ = _c(46);
  const {
    row: t1,
    root: t2,
    index: t3,
    children,
    onRowSelectionChange
  } = t0;
  const {
    id,
    subRows,
    toggleExpanded,
    getCanExpand,
    getIsExpanded,
    getVisibleCells
  } = t1;
  const rootRef = t2 === undefined ? null : t2;
  const index = t3 === undefined ? 0 : t3;
  const rowRef = useRef(null);
  const {
    height: rowHeight
  } = useRect(rowRef);
  const detailRef = useRef(null);
  const [open, setOpen] = useState(getIsExpanded);
  const [animationOpen, setAnimationOpen] = useState(false);
  const retimeAnim = useRetimer();
  const [transitionOpen, setTransitionOpen] = useState(false);
  const retimeTransition = useRetimer();
  let t4;
  if ($[0] !== retimeAnim || $[1] !== retimeTransition || $[2] !== rootRef || $[3] !== rowHeight || $[4] !== subRows || $[5] !== toggleExpanded) {
    t4 = function handleToggleExpanded(isClosing) {
      const isOpening = !isClosing;
      setAnimationOpen(true);
      setTransitionOpen(true);
      setOpen(isOpening);
      toggleExpanded();
      if (isOpening) {
        retimeAnim(() => setAnimationOpen(false), ANIM_DURATION);
      }
      retimeTransition(() => setTransitionOpen(false), SHADOW_DURATION);
      if (rootRef?.current && rowRef.current) {
        if (rowRef.current.getBoundingClientRect().y - rootRef.current.getBoundingClientRect().y <= HEAD_HEIGHT) {
          if (isClosing && detailRef.current) {
            const subRowsHeight = sum(subRows.map(_temp).map(_temp2));
            const overlapHeight = rowRef.current.offsetTop + rowHeight - detailRef.current.offsetTop;
            const expandedHeight = detailRef.current.offsetHeight + subRowsHeight;
            if (overlapHeight < expandedHeight) {
              rootRef?.current.scrollTo({
                top: -HEAD_HEIGHT - rowHeight + detailRef.current.offsetTop
              });
            } else {
              rootRef.current.scrollTo({
                top: -HEAD_HEIGHT + rowRef.current.offsetTop - expandedHeight,
                behavior: isClosing ? "instant" : "smooth"
              });
            }
          } else {
            rootRef.current.scrollTo({
              top: -HEAD_HEIGHT + rowRef.current.offsetTop,
              behavior: "smooth"
            });
          }
        }
      }
    };
    $[0] = retimeAnim;
    $[1] = retimeTransition;
    $[2] = rootRef;
    $[3] = rowHeight;
    $[4] = subRows;
    $[5] = toggleExpanded;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleToggleExpanded = t4;
  let t5;
  if ($[7] !== getCanExpand) {
    t5 = getCanExpand();
    $[7] = getCanExpand;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const canExpand = t5;
  const t6 = `${nstr(-HEAD_HEIGHT - rowHeight)}px 0% 0%`;
  let t7;
  if ($[9] !== rootRef || $[10] !== t6) {
    t7 = {
      root: rootRef,
      rootMargin: t6
    };
    $[9] = rootRef;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const detailEntry = useIntersectionObserver(canExpand ? detailRef : null, t7);
  const isDetailAboveRoot = detailEntry && detailEntry.boundingClientRect.bottom < (detailEntry.rootBounds?.top ?? 0);
  const t8 = `${nstr(-HEAD_HEIGHT - rowHeight + 1)}px 0% -100%`;
  let t9;
  if ($[12] !== rootRef || $[13] !== t8) {
    t9 = {
      root: rootRef,
      rootMargin: t8
    };
    $[12] = rootRef;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const detailEntry2 = useIntersectionObserver(canExpand ? detailRef : null, t9);
  const isDetailVisibleIntersecting = Boolean(detailEntry2?.isIntersecting);
  let t10;
  if ($[15] !== getVisibleCells) {
    t10 = getVisibleCells();
    $[15] = getVisibleCells;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const visibleCells = useClone(t10);
  const state = open ? "open" : "closed";
  let rowZIndex = index;
  if (open && subRows.length >= 1) {
    rowZIndex = rowZIndex + (subRows.length + 1);
  }
  let t11;
  if ($[17] !== rowZIndex) {
    t11 = {
      "--top": `${HEAD_HEIGHT}px`,
      "--z-index": rowZIndex
    };
    $[17] = rowZIndex;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = !canExpand || undefined;
  const t13 = (isDetailAboveRoot ? open : isDetailVisibleIntersecting) && !animationOpen ? "" : undefined;
  let t14;
  if ($[19] !== handleToggleExpanded || $[20] !== onRowSelectionChange || $[21] !== visibleCells) {
    let t15;
    if ($[23] !== handleToggleExpanded || $[24] !== onRowSelectionChange) {
      t15 = cell => <FlexRender key={cell.id} comp={cell.column.columnDef.cell} props={{
        ...cell.getContext(),
        onExpandedChange: handleToggleExpanded,
        onRowSelectionChange
      }} />;
      $[23] = handleToggleExpanded;
      $[24] = onRowSelectionChange;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    t14 = visibleCells.map(t15);
    $[19] = handleToggleExpanded;
    $[20] = onRowSelectionChange;
    $[21] = visibleCells;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[26] !== id || $[27] !== state || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14) {
    t15 = <tr ref={rowRef} data-id={id} style={t11} data-row={true} className="group/tr relative z-(--z-index) bg-background transition-shadow duration-0 [content-visibility:auto] data-boundary:shadow-intersect! data-[state=open]:sticky data-[state=open]:top-(--top) [[data-boundary]+*:empty+&>*]:border-t-transparent [[data-detail-above]+&]:shadow-collapse [[data-detail-above]+&]:duration-200 [[data-state=closed]+&]:shadow-collapse [[data-state=closed][data-disabled]+&]:shadow-none [[data-transition-open]+&]:duration-300" data-disabled={t12} data-state={state} data-boundary={t13}>{t14}</tr>;
    $[26] = id;
    $[27] = state;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== animationOpen || $[34] !== canExpand || $[35] !== children || $[36] !== index || $[37] !== isDetailAboveRoot || $[38] !== open || $[39] !== state || $[40] !== transitionOpen || $[41] !== visibleCells.length) {
    t16 = canExpand ? <tr ref={detailRef} style={{
      "--z-index": index - 1
    }} data-state={state} data-detail-above={isDetailAboveRoot || undefined} data-transition-open={transitionOpen || undefined} className="relative z-(--z-index) bg-background">{children ? <td colSpan={visibleCells.length} className="p-0"><Collapsible open={open}><CollapsibleContent data-no-anim-open={!animationOpen || undefined} data-no-anim-closed={isDetailAboveRoot && !open || undefined} className="overflow-hidden data-closed:animate-accordion-up data-no-anim-closed:data-closed:animation-duration-0 data-open:animate-accordion-down data-no-anim-open:data-open:animation-duration-0">{children}</CollapsibleContent></Collapsible></td> : null}</tr> : null;
    $[33] = animationOpen;
    $[34] = canExpand;
    $[35] = children;
    $[36] = index;
    $[37] = isDetailAboveRoot;
    $[38] = open;
    $[39] = state;
    $[40] = transitionOpen;
    $[41] = visibleCells.length;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t16) {
    t17 = <Fragment>{t15}{t16}</Fragment>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
}
function _temp2(e) {
  return e?.offsetHeight || 0;
}
function _temp(subRow) {
  return document.querySelector(`tr[data-id="${subRow.id}"]`);
}